import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, InputBase } from "@mui/material";
import axios from "axios";
import {
  formatPhoneNumber,
  formatPhoneNumberAsYouType,
} from "../utils/formatnumber";

/** True when the string contains letters (name/company search), not just a phone. */
export function inputLooksLikeName(value) {
  return /[a-zA-Z]/.test(value || "");
}

/** Digits only, for comparing formatted vs E.164 numbers. */
export function digitsOnly(value) {
  return (value || "").replace(/\D/g, "");
}

/**
 * Look up a contact by phone number via search-contacts.
 * Returns the best matching contact or null.
 */
export async function resolveContactByPhone(organizationId, phone) {
  if (!organizationId || !phone?.trim()) return null;

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}search-contacts`,
      {
        organizationId,
        query: phone.trim(),
        limit: 8,
      }
    );

    if (response.data?.status !== "success") return null;

    const contacts = response.data.contacts || [];
    if (contacts.length === 0) return null;

    const needle = digitsOnly(phone);
    const exact = contacts.find((c) => {
      const digits = digitsOnly(c.phoneNumber);
      return (
        digits === needle ||
        digits.endsWith(needle) ||
        needle.endsWith(digits)
      );
    });
    if (exact) return exact;
    if (contacts.length === 1) return contacts[0];
    return null;
  } catch (err) {
    console.error("Failed to resolve contact by phone:", err);
    return null;
  }
}

function contactMatchesRecipient(contact, recipient) {
  if (!contact?.phoneNumber || !recipient) return false;
  const a = digitsOnly(contact.phoneNumber);
  const b = digitsOnly(recipient);
  if (!a || !b) return false;
  return a === b || a.endsWith(b) || b.endsWith(a);
}

function formatContactSubtitle(contact) {
  const phone = formatPhoneNumber(contact.phoneNumber) || contact.phoneNumber;
  const company = (contact.company || "").trim();
  if (company) return `${company} · ${phone}`;
  return phone;
}

function formatCallingLine(contact) {
  const name = (contact.name || "").trim() || "Unknown";
  const company = (contact.company || "").trim();
  if (company) return `Calling ${name} · ${company}`;
  return `Calling ${name}`;
}

/**
 * Recipient field with contact name/number lookup.
 * freeSolo so unknown numbers remain dialable.
 */
export default function RecipientContactInput({
  value,
  onChange,
  selectedContact,
  onSelectedContactChange,
  organizationId,
  error,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  // Keep input in sync when parent sets value (URL/Front prefill)
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const searchContacts = useCallback(
    async (query) => {
      const trimmed = (query || "").trim();
      if (!organizationId || trimmed.length < 2) {
        setOptions([]);
        setLoading(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}search-contacts`,
          {
            organizationId,
            query: trimmed,
            limit: 8,
          }
        );

        if (requestId !== requestIdRef.current) return;

        if (response.data?.status === "success") {
          setOptions(response.data.contacts || []);
        } else {
          setOptions([]);
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Contact search failed:", err);
        setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [organizationId]
  );

  const scheduleSearch = useCallback(
    (query) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        searchContacts(query);
      }, 350);
    },
    [searchContacts]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const clearSelectionIfMismatch = (nextValue) => {
    if (
      selectedContact &&
      !contactMatchesRecipient(selectedContact, nextValue)
    ) {
      onSelectedContactChange(null);
    }
  };

  return (
    <div className="w-full">
      <Autocomplete
        freeSolo
        options={options}
        loading={loading}
        filterOptions={(opts) => opts}
        // Control text only; selectedContact is confirmation state in the parent
        inputValue={inputValue}
        value={null}
        disableClearable
        getOptionLabel={(option) => {
          if (option == null) return "";
          if (typeof option === "string") return option;
          const formatted = formatPhoneNumber(option.phoneNumber);
          return (
            (typeof formatted === "string" && formatted) ||
            option.phoneNumber ||
            option.name ||
            ""
          );
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (typeof val === "string") {
            return digitsOnly(option.phoneNumber) === digitsOnly(val);
          }
          return option.phoneNumber === val?.phoneNumber;
        }}
        onInputChange={(event, newInputValue, reason) => {
          // Only apply user keystrokes. Ignore reset/blur/selectOption churn from
          // Autocomplete freeSolo + value={null}, which can wipe URL/Front prefills.
          if (reason !== "input") return;

          let next = newInputValue ?? "";
          if (next === "undefined") next = "";
          if (!inputLooksLikeName(next)) {
            next = formatPhoneNumberAsYouType(next);
          }

          setInputValue(next);
          onChange(next);
          clearSelectionIfMismatch(next);

          if (!next.trim()) {
            setOptions([]);
            onSelectedContactChange(null);
            return;
          }

          scheduleSearch(next);
        }}
        onChange={(event, newValue) => {
          if (newValue && typeof newValue === "object" && newValue.phoneNumber) {
            const formatted =
              formatPhoneNumber(newValue.phoneNumber) || newValue.phoneNumber;
            setInputValue(formatted);
            onChange(formatted);
            onSelectedContactChange(newValue);
            setOptions([]);
            return;
          }

          if (typeof newValue === "string") {
            const next = inputLooksLikeName(newValue)
              ? newValue
              : formatPhoneNumberAsYouType(newValue);
            setInputValue(next);
            onChange(next);
            clearSelectionIfMismatch(next);
          }
          // Ignore null clears from Autocomplete reconciling value={null}
        }}
        noOptionsText={
          inputValue.trim().length < 2
            ? "Type a name or number"
            : "No matching contacts"
        }
        renderOption={(props, option) => (
          <li {...props} key={option.phoneNumber}>
            <div className="py-1">
              <div className="text-[14px] font-semibold text-gray-800">
                {option.name || "Unknown"}
              </div>
              <div className="text-[12px] text-gray-500">
                {formatContactSubtitle(option)}
              </div>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <InputBase
            id={params.id}
            disabled={params.disabled}
            ref={params.InputProps.ref}
            className={params.InputProps.className}
            inputProps={params.inputProps}
            placeholder="Name or phone number"
            sx={{
              bgcolor: "white",
              width: "100%",
              px: 2,
              py: 1,
              fontSize: "16px",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
            }}
            endAdornment={
              <>
                {loading ? (
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            }
          />
        )}
        fullWidth
      />
      {error && (
        <div className="mt-1.5 text-[12px] text-red-600">{error}</div>
      )}
      {selectedContact && contactMatchesRecipient(selectedContact, value) && (
        <div className="mt-1.5 text-[12px] text-gray-500">
          {formatCallingLine(selectedContact)}
        </div>
      )}
    </div>
  );
}
